import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { _FormFieldDescription } from "@@/shared/form/_form-field-description";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { FormErrorMessages } from "@@/shared/form/form2-error-messages";
import { Icon } from "@@/shared/icons/icon";
import type { FontWeight } from "@@/styles/theme";
import { useTheme } from "@emotion/react";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { faSquare } from "@fortawesome/pro-light-svg-icons";
import { faLock } from "@fortawesome/pro-regular-svg-icons";
import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { faAsterisk, faCheckSquare } from "@fortawesome/pro-solid-svg-icons";
import type {
    Color,
    ColorSet,
    DeepKeysOf,
    RemSize,
    Translatable,
    TranslatableType,
    TranslatableValueFactoryParams,
} from "@towni/common";
import {
    isTranslatable,
    isTranslatableValueFactoryParams,
} from "@towni/common";
import { useState } from "react";
import type { ZodSchema } from "zod";
import { Paragraph, paragraphIconFactory } from "../text";
import type { Form3ErrorMessage } from "./form3.error-message";
import { useForm3Field } from "./form3.use-form-field";
import { useFormColors } from "./use-form-colors";

type ValueType = boolean;

type Props<FormData, FieldId extends DeepKeysOf<FormData>> = {
    readonly className?: string;
    readonly fieldId: FieldId;
    readonly formId?: FormId;
    readonly text?: TranslatableType | React.JSX.Element;
    readonly size?: RemSize;
    readonly description?: Translatable | TranslatableValueFactoryParams;
    readonly hideDescriptionAfterInput?: boolean;
    readonly lineClamp?: number;
    readonly disabled?: boolean;
    readonly locked?: boolean;
    readonly spin?: boolean;
    readonly checkedIcon?: IconDefinition;
    readonly uncheckedIcon?: IconDefinition;
    readonly colorSet?: ColorSet;
    readonly showAsRequired?: boolean;
    /** Defaults to theme.primary */
    readonly accentColor?: Color;
    readonly weight?: FontWeight;
};

const createForm3CheckboxComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3_Checkbox = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
    >(
        props: Props<FormData, FieldId>,
    ) => {
        const theme = useTheme();
        const [errors, setErrors] = useState<Form3ErrorMessage[]>([]);
        const hasErrors = errors.length > 0;
        const field = useForm3Field<FormSchema, FormData, FieldId, ValueType>({
            fieldId: props.fieldId,
            formId: props.formId,
        });

        const size = props.size ?? (1 as RemSize);
        const disabled =
            props.disabled || props.spin || field.form.mutation?.isPending;
        const { backgroundColor, textColor } = useFormColors({
            hasErrors,
            colorSet: props.colorSet,
        });
        const accentColor = props.accentColor ?? theme.colors.primary;
        const disabledOrLocked = disabled || !!props.locked;
        const cursor = disabledOrLocked ? "default" : "pointer";

        return (
            <FlexColumn
                crossAxis="stretch"
                className={props.className}
                tag={props.fieldId}>
                <FlexRow
                    fillParentWidth
                    mainAxis="space-between"
                    css={{ position: "relative" }}
                    crossAxis="center">
                    <label
                        css={{
                            cursor,
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                        }}>
                        <input
                            type="checkbox"
                            disabled={disabledOrLocked}
                            onBlur={() => {
                                field.setTouched(true);
                                setErrors(field.validate());
                            }}
                            checked={!!field.value}
                            onChange={element => {
                                field.setValue(element.target.checked);
                                field.setTouched(true);
                                field.setDirty(
                                    element.target.checked !==
                                        field.initialValue,
                                );
                                setErrors(field.validate());
                            }}
                            css={{
                                cursor,
                                opacity: 0,
                                display: "block",
                                height: 0,
                                width: 0,
                                position: "absolute",
                                overflow: "hidden",
                            }}
                        />
                        <span
                            css={{
                                cursor,
                                display: "inline-block",
                            }}>
                            <Icon
                                icon={
                                    props.spin
                                        ? faSpinnerThird
                                        : field.value
                                          ? (props.checkedIcon ?? faCheckSquare)
                                          : (props.uncheckedIcon ?? faSquare)
                                }
                                color={
                                    field.value
                                        ? accentColor
                                        : theme.colors.black.light70
                                }
                                spin={props.spin}
                                style={{
                                    filter: props.locked
                                        ? "grayscale(100%)"
                                        : "",
                                    backgroundColor,
                                    fontSize: size * 1.125 * theme.sizes.base,
                                    opacity: disabledOrLocked ? 0.3 : 1,
                                }}
                            />
                        </span>
                        {isTranslatable(props.text) ||
                        isTranslatableValueFactoryParams(props.text) ||
                        typeof props.text === "undefined" ? (
                            <Paragraph
                                content={[
                                    props.text || "",
                                    ...(props.showAsRequired
                                        ? [
                                              " ",
                                              paragraphIconFactory({
                                                  icon: faAsterisk,
                                                  faIconProps: {
                                                      fontSize: `${size * 0.5}rem`,
                                                      style: {
                                                          position: "relative",
                                                          left: 2,
                                                          top: -4,
                                                      },
                                                      color: theme.colors.danger
                                                          .asString,
                                                  },
                                              }),
                                          ]
                                        : []),
                                ]}
                                css={{
                                    padding: 0,
                                    paddingLeft: 8,
                                    fontWeight: props.weight ?? "400",
                                    fontSize: size * theme.sizes.base,
                                    whiteSpace: "pre-wrap",
                                    overflow: "hidden",
                                    color: textColor,
                                    opacity: disabledOrLocked ? 0.3 : 1,
                                }}
                            />
                        ) : (
                            <>{props.text}</>
                        )}
                    </label>
                    <Conditional when={!!props.locked}>
                        <HorizontalDivider />
                        <Icon icon={faLock} css={{ opacity: 0.5 }} />
                    </Conditional>
                </FlexRow>
                <_FormFieldDescription
                    hasErrors={hasErrors}
                    isDirty={field.dirty}
                    description={props.description}
                    hideDescriptionAfterInput={
                        !!props.hideDescriptionAfterInput
                    }
                    css={{
                        paddingLeft: 0,
                        marginTop: 5,
                        opacity: disabledOrLocked ? 0.5 : 1,
                    }}
                />
                <FormErrorMessages
                    errors={errors}
                    css={{
                        opacity: disabledOrLocked ? 0.5 : 1,
                    }}
                />
            </FlexColumn>
        );
    };
    return Form3_Checkbox;
};

export { createForm3CheckboxComponent };
export type { Props as Form3_CheckboxProps };
